/* Repository path: frontend/src/api/feedback.ts */

import type { FeedbackAccessContext, FeedbackCategory, FeedbackSurface } from "../types";
import { request } from "./client";

/**
 * The SPA build version, stamped at Docker build time from
 * `APP_BUILD_VERSION` into `import.meta.env.VITE_APP_VERSION` (see the
 * `frontend-builder` stage in the repository `Dockerfile`). Falls back to
 * `"dev"` outside a built image — never thrown, never left blank, since a
 * feedback submission must succeed either way. A malformed value degrades
 * server-side to `"unknown"` rather than rejecting the note; see
 * docs/architecture/features/W4-08.md §5.
 */
export function appVersion(): string {
  const value = import.meta.env.VITE_APP_VERSION;
  return typeof value === "string" && value.trim() ? value : "dev";
}

export interface FeedbackSubmission {
  category: FeedbackCategory;
  surface: FeedbackSurface;
  accessContext: FeedbackAccessContext;
  /** The canonical item code when launched from a card or detail —
   * `null` otherwise. Never the item's name or any other display text. */
  itemCode: string | null;
  /** Trimmed free text, or `null` for none. */
  message: string | null;
  /** A whole number 1–5, or `null` for "not answered". */
  rating: number | null;
}

/**
 * `POST /api/v1/feedback` (W4-08) — one anonymous, unlinked note. No
 * `Idempotency-Key`: a duplicate note costs one row, and a client-generated
 * key persisted on it would itself be a correlation handle — see
 * docs/architecture/adr/ADR-0012-unlinked-anonymous-feedback.md decision 5.
 * The request body is built here as exactly these seven keys and nothing
 * else, regardless of what the caller passes, so "no raw sensitive
 * plan/profile payload is attached automatically" holds even if a future
 * caller is careless. The response carries no data to validate — a
 * successful call resolves to `{ status }` and nothing else.
 */
export function submitFeedback(input: FeedbackSubmission): Promise<{ status: string }> {
  return request<{ status: string }>("/feedback", {
    method: "POST",
    body: {
      category: input.category,
      surface: input.surface,
      access_context: input.accessContext,
      item_code: input.itemCode,
      message: input.message,
      rating: input.rating,
      client_version: appVersion(),
    },
  });
}
